import { jsx } from '@amazon/vinyl-tsx'
import { data, type MutableValue } from '@amazon/vinyl-observable'
import { Icon } from '@/components/Icon'
import { createDisposer, type Maybe } from '@amazon/vinyl-util'
import type { TrackFields } from '@/track/trackFieldsFrag'
import { TrackOverflowMenu } from '@/track/TrackOverflowMenu'
import type { PlayQueueBindingDeps } from '@/playback/playQueueBinding'
import { ImageSizes } from '@/style/styleCommon'
import { getLargestImage } from '@/util/imageUtil'
import { createImageModel } from '@/util/ImageUrl'
import { getTrackArtistStr } from '@/track/trackGqlUtil'
import { AddToPlaylistModal } from '@/library/AddToPlaylistModal'
import { openShareModal } from '@/detailpage/globalShareModal'

export interface TrackOverflowButtonProps {
    readonly data: MutableValue<Maybe<TrackFields>>
    /**
     * Optional. When provided, the menu wires Play Next + Add to
     * Queue against the controller's live queue. Without it those
     * actions are disabled.
     */
    readonly deps?: PlayQueueBindingDeps
    /**
     * Pass true on the transport's instance — the menu then
     * surfaces Previous / Shuffle / Repeat at narrow widths
     * (matching the breakpoint that hides those inline buttons).
     * Row-level overflows leave this off.
     */
    readonly transport?: boolean
}

export function TrackOverflowButton(props: TrackOverflowButtonProps) {
    const visible = data(false)
    const addToPlaylistVisible = data(false)
    const addToPlaylistTrackId = data<string | null>(null)
    let buttonEl: HTMLButtonElement | null = null

    function openShare() {
        const track = props.data.value
        if (!track) return
        const asin = track.playParams?.id
        if (!asin) return
        const url = new URL(window.location.href)
        url.pathname = `/tracks/${asin}`
        url.search = ''
        url.hash = ''
        const primaryImage =
            getLargestImage(track.images, 'PRIMARY') ?? getLargestImage(track.images)
        openShareModal({
            title: track.title ?? null,
            secondary: getTrackArtistStr(track) || null,
            link: url.toString(),
            label: 'track',
            entityType: 'track',
            description: track.album?.title ?? null,
            image: createImageModel(primaryImage, { scaleToSquare: ImageSizes.LARGE }),
        })
    }

    return (
        <span className="trackOverflowAnchor">
            <button
                className="iconButton trackOverflowButton"
                aria-haspopup="menu"
                aria-expanded={visible}
                onclick={(event) => {
                    event.stopPropagation()
                    visible.value = !visible.value
                }}
                onConnect={(btn) => {
                    buttonEl = btn as HTMLButtonElement
                }}
            >
                <Icon icon="more" />
            </button>
            <TrackOverflowMenu
                visible={visible}
                track={props.data}
                getAnchor={() => buttonEl}
                onShare={openShare}
                onAddToPlaylist={() => {
                    // Capture the track id at click time so the modal
                    // has a stable target even if a virtualized row
                    // recycles while the modal is open.
                    addToPlaylistTrackId.value = props.data.value?.playParams?.id ?? null
                    addToPlaylistVisible.value = true
                }}
                {...(props.deps ? { deps: props.deps } : {})}
                {...(props.transport != null ? { transport: props.transport } : {})}
            />
            <div
                className="trackAddToPlaylistModalPortal"
                onConnect={(view) => {
                    // Same `contain: strict` escape hatch the share
                    // modal used to need before it moved up to the
                    // AppView level — without reparenting to body the
                    // virtualized list clips the fixed-position
                    // overlay to the row.
                    const originalParent = view.parentNode
                    const placeholder = document.createComment('trackAddToPlaylistModal')
                    if (originalParent) originalParent.insertBefore(placeholder, view)
                    document.body.appendChild(view)
                    const { add, dispose } = createDisposer()
                    add(() => {
                        if (placeholder.parentNode) {
                            placeholder.parentNode.insertBefore(view, placeholder)
                            placeholder.remove()
                        } else if (view.parentNode === document.body) {
                            document.body.removeChild(view)
                        }
                    })
                    return dispose
                }}
            >
                <AddToPlaylistModal visible={addToPlaylistVisible} trackId={addToPlaylistTrackId} />
            </div>
        </span>
    )
}
